import React, { useCallback, useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { closeView, setAsel, setViewPosition, viewPositionSelector } from "~redux/slices/edstSlice";
import { openMenuThunk } from "~redux/thunks/openMenuThunk";
import { useDragging } from "hooks/useDragging";
import {
  colorNameMap,
  edstFontName,
  getBitmapTextStyles,
  useCenterCursor,
  useFocused,
  usePixiOnClickOutside,
} from "@poscon/shared-frontend";
import type { EdstView } from "types/edstView";
import { eramHubConnection } from "~/eramHubConnection";
import type { Container as PixiContainer } from "pixi.js";
import { isString } from "@poscon/shared-types";
import { MenuTitleBar } from "components/utils/MenuTitleBar";
import { defaultTint, disabledTint, edstFontDim } from "~/utils/constants";
import { layerZIndexMap } from "~/layerZIndexMap";
import { TableField } from "components/utils/TableField";
import type { RootThunkAction } from "~redux/store";
import { EramFlightplan, isDepRowField } from "@poscon/shared-types/eram";
import { InteractiveContainer } from "../utils/InteractiveContainer";
import { EdstAircraftMenuProps } from "../menus/AircraftMenu";
import { updateEdstFpData } from "~/redux/thunks/processEramMessage";
import { Asel } from "~/types/asel";
import { EdstButton } from "../utils/EdstButton";

type PlanOptionItem = {
  y: number;
  text: string | ((fp: EramFlightplan) => string);
  disabled?: boolean | ((fp: EramFlightplan) => boolean);
  action?: (asel: Asel, fp: EramFlightplan) => RootThunkAction;
};

const depItems: PlanOptionItem[] = [];
const aclItems: PlanOptionItem[] = [
  {
    y: 0,
    text: "Altitude...",
    action: (asel) => (dispatch, getState) => {
      const pos = viewPositionSelector(getState(), "PLAN_OPTIONS");
      dispatch(setViewPosition({ view: "ALTITUDE_MENU", pos }));
      dispatch(setAsel({ ...asel, menu: "ALTITUDE_MENU" }));
    },
  },
  {
    y: edstFontDim.height + 4,
    text: "Speed...",
    action: (asel) => (dispatch, getState) => {
      const pos = viewPositionSelector(getState(), "PLAN_OPTIONS");
      dispatch(setViewPosition({ view: "SPEED_MENU", pos }));
      dispatch(setAsel({ ...asel, menu: "SPEED_MENU" }));
    },
  },
  {
    y: (edstFontDim.height + 4) * 2,
    text: "Route...",
    action: (asel) => (dispatch, getState) => {
      const pos = viewPositionSelector(getState(), "PLAN_OPTIONS");
      dispatch(setViewPosition({ view: "ROUTE_MENU", pos }));
      dispatch(setAsel({ ...asel, menu: "ROUTE_MENU" }));
    },
  },
  {
    y: (edstFontDim.height + 4) * 3,
    text: "Previous Route",
    disabled: (fp) => !fp.previousRoute,
    action: (asel) => (dispatch, getState) => {
      const pos = viewPositionSelector(getState(), "PLAN_OPTIONS");
      dispatch(setViewPosition({ view: "PREV_ROUTE_MENU", pos }));
      dispatch(setAsel({ ...asel, menu: "PREV_ROUTE_MENU" }));
    },
  },
  {
    y: (edstFontDim.height + 4) * 4,
    text: (fp) => `${fp.probeInhibit ? "Resume" : "Stop"} Probe...`,
    action: (asel, fp) => (dispatch, getState) => {
      if (fp.probeInhibit) {
        eramHubConnection.emit("resumeFpProbe", fp.id);
        dispatch(setAsel(null));
      } else {
        const pos = viewPositionSelector(getState(), "PLAN_OPTIONS");
        dispatch(setViewPosition({ view: "PROBE_MENU", pos }));
        dispatch(setAsel({ ...asel, menu: "PROBE_MENU" }));
      }
    },
  },
  {
    y: (edstFontDim.height + 4) * 5,
    text: "Trial Restrictions...",
    disabled: true,
  },
  { y: (edstFontDim.height + 4) * 6, text: "Plans", disabled: true },
  { y: (edstFontDim.height + 4) * 7, text: "Resubmit...", disabled: true },
  { y: (edstFontDim.height + 4) * 8, text: "Keep", disabled: true },
  {
    y: (edstFontDim.height + 4) * 9,
    text: "Delete",
    action: (asel) => (dispatch) => {
      eramHubConnection.emit("deleteEdstFp", asel.fpId);
      dispatch(setAsel(null));
    },
  },
];

const width = edstFontDim.width * 24;

type PlanOptionsProps = EdstAircraftMenuProps;
export const PlanOptions = ({ fp, asel }: PlanOptionsProps) => {
  const dispatch = useRootDispatch();

  const pos = useRootSelector((state) => viewPositionSelector(state, "PLAN_OPTIONS"));
  const ref = useRef<PixiContainer>(null);
  const dep = isDepRowField(asel.field);
  const focused = useFocused(ref);
  useCenterCursor(ref);
  const { startDrag } = useDragging(ref, "PLAN_OPTIONS", "mouseup");

  usePixiOnClickOutside(ref, () => dispatch(setAsel(null)));

  const onKeepClick = () => {
    dispatch(updateEdstFpData(fp.id, { keep: true }));
  };

  const items = dep ? depItems : aclItems;
  const height = edstFontDim.height * 5 + 12 + (items.at(-1)?.y ?? 0);

  return (
    <InteractiveContainer ref={ref} x={pos.x} y={pos.y} zIndex={layerZIndexMap.prompts} sortableChildren>
      <MenuTitleBar
        title="Plan Options"
        width={width}
        fontName={edstFontName}
        fill={focused ? colorNameMap.cyan : colorNameMap.mediumGrey}
        withX={false}
        onTitleMouseDown={startDrag}
        view="PLAN_OPTIONS"
      />
      <container y={edstFontDim.height + 3}>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: colorNameMap.grey });
          }}
        />
        <bitmapText
          x={Math.floor(width / 2)}
          y={10}
          text={`${fp.cid} ${fp.callsign}`}
          roundPixels
          anchor={{ x: 0.5, y: 0 }}
          style={{ ...getBitmapTextStyles(edstFontName), fill: defaultTint }}
        />
        <container x={8} y={edstFontDim.height * 2 + 8}>
          {aclItems.map(({ y, text, disabled, action }, i) => {
            const isDisabled = typeof disabled === "function" ? disabled(fp) : disabled;
            return (
              <TableField
                key={i}
                textX={2}
                y={y}
                width={width - 17}
                text={isString(text) ? text : text(fp)}
                tint={isDisabled ? disabledTint : defaultTint}
                disabled={isDisabled}
                onmousedown={() => {
                  if (action && !isDisabled) {
                    dispatch(action(asel, fp));
                  }
                }}
              />
            );
          })}
        </container>
        <EdstButton
          x={width - edstFontDim.width * 4 - 15}
          y={height - (edstFontDim.height + 8)}
          text="Exit"
          onmousedown={() => dispatch(setAsel(null))}
        />
      </container>
    </InteractiveContainer>
  );
};
